import * as React from 'react'

import { IconBell } from '@tabler/icons-react'

import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Badge } from '@/components/sui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/sui/popover'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'

import { formatBadgeCount } from './format-badge-count'
import locales from './locales'
import { NotificationsPanel } from './notifications-panel'

interface NotificationsPopoverProps {
  unreadCount?: number
}

export function NotificationsPopover({
  unreadCount = 0,
}: NotificationsPopoverProps) {
  const { t } = useComponentTranslation(locales)
  const { isMobile, setOpenMobile, state } = useSidebar()
  const [open, setOpen] = React.useState(false)

  const itemContent = (
    <>
      <IconBell />
      <span>{t('title')}</span>
      {unreadCount > 0 && (
        <>
          <Badge
            variant="danger"
            size="count"
            className="ml-auto group-data-[collapsible=icon]:hidden"
          >
            {formatBadgeCount(unreadCount)}
          </Badge>
          <span className="bg-destructive absolute top-1 right-1 hidden size-2 rounded-full group-data-[collapsible=icon]:block" />
        </>
      )}
    </>
  )

  // Mobile: the sidebar itself is a sheet, so an anchored popover has nothing
  // stable to anchor to — open a bottom drawer over it instead. The sidebar
  // sheet MUST stay open: closing it unmounts this item (and the drawer with
  // it) before the drawer can show. Both close together on navigation.
  if (isMobile) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          size="sm"
          className="relative cursor-pointer"
          onClick={() => setOpen(true)}
        >
          {itemContent}
        </SidebarMenuButton>
        <ResponsiveDialog open={open} onOpenChange={setOpen} surface="drawer">
          <ResponsiveDialogContent>
            <ResponsiveDialogHeader visuallyHidden>
              <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                {t('description')}
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>
            <ResponsiveDialogBody flush>
              <NotificationsPanel
                onNavigate={() => {
                  setOpen(false)
                  setOpenMobile(false)
                }}
              />
            </ResponsiveDialogBody>
          </ResponsiveDialogContent>
        </ResponsiveDialog>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger
            render={
              <PopoverTrigger
                render={
                  <SidebarMenuButton
                    size="sm"
                    className="data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground relative cursor-pointer"
                  />
                }
              />
            }
          >
            {itemContent}
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="center"
            hidden={state !== 'collapsed'}
          >
            {t('title')}
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-96 gap-0 overflow-hidden rounded-xl p-0"
          side="right"
          align="end"
          sideOffset={8}
        >
          <NotificationsPanel onNavigate={() => setOpen(false)} />
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  )
}
